import React from 'react';
import { 
  Eye, 
  AlertTriangle, 
  CloudLightning, 
  ShieldAlert, 
  UserX, 
  Camera 
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

// Data source configuration
export const DATA_SOURCES = {
  incidents: { label: 'Emergency Incidents', icon: AlertTriangle, color: '#ef4444', enabled: true },
  weather: { label: 'Weather Alerts', icon: CloudLightning, color: '#06b6d4', enabled: true },
  ice: { label: 'ICE Activity', icon: ShieldAlert, color: '#f97316', enabled: true },
  offenders: { label: 'Sex Offenders', icon: UserX, color: '#dc2626', enabled: false, tier: 'amateur' },
  cameras: { label: 'Security Cameras', icon: Camera, color: '#6b7280', enabled: false, tier: 'amateur' }
};

const DataSourceToggle = ({ 
  activeDataSources, 
  toggleDataSource, 
  userTier,
  currentStats,
  canAccessOffenders,
  canAccessCameras 
}) => {
  return (
    <div className="p-4 border-b border-slate-800">
      <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider flex items-center gap-2 mb-3">
        <Eye className="w-4 h-4 text-teal-400" />
        Data Sources
      </h3>
      <div className="space-y-2">
        {Object.entries(DATA_SOURCES).map(([key, source]) => {
          const Icon = source.icon;
          const isLocked = source.tier && !['amateur', 'professional', 'business'].includes(userTier);
          const isActive = activeDataSources.includes(key);
          
          return (
            <button
              key={key}
              onClick={() => !isLocked && toggleDataSource(key)}
              disabled={isLocked}
              className={`w-full flex items-center justify-between p-2 rounded transition-all ${
                isLocked 
                  ? 'opacity-50 cursor-not-allowed'
                  : isActive 
                    ? 'bg-slate-800 border border-slate-700' 
                    : 'hover:bg-slate-800/50'
              }`}
              data-testid={`toggle-${key}`}
            >
              <div className="flex items-center gap-2">
                <div 
                  className="w-6 h-6 rounded flex items-center justify-center"
                  style={{ backgroundColor: `${source.color}20` }}
                >
                  <Icon className="w-3.5 h-3.5" style={{ color: source.color }} />
                </div>
                <span className="text-sm text-slate-300">{source.label}</span>
              </div>
              <div className="flex items-center gap-2">
                {isLocked && (
                  <Badge className="bg-amber-600/20 text-amber-400 text-[10px]">PRO</Badge>
                )}
                {!isLocked && (
                  <div className={`w-4 h-4 rounded-full border-2 transition-all ${
                    isActive 
                      ? 'bg-teal-500 border-teal-500' 
                      : 'border-slate-600'
                  }`}>
                    {isActive && <div className="w-full h-full flex items-center justify-center">
                      <div className="w-1.5 h-1.5 bg-white rounded-full" />
                    </div>}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
      
      {/* Data source counts */}
      <div className="mt-3 pt-3 border-t border-slate-800 grid grid-cols-2 gap-2">
        {activeDataSources.includes('weather') && (
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <CloudLightning className="w-3 h-3 text-cyan-400" />
            <span>{currentStats.weather} alerts</span>
          </div>
        )}
        {activeDataSources.includes('ice') && (
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <ShieldAlert className="w-3 h-3 text-orange-400" />
            <span>{currentStats.ice} reports</span>
          </div>
        )}
        {activeDataSources.includes('offenders') && canAccessOffenders && (
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <UserX className="w-3 h-3 text-red-400" />
            <span>{currentStats.offenders} registered</span>
          </div>
        )}
        {activeDataSources.includes('cameras') && canAccessCameras && (
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <Camera className="w-3 h-3 text-slate-400" />
            <span>{currentStats.cameras} cameras</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataSourceToggle;
